import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/password-input';
import { Loader2 } from 'lucide-react';

interface PractitionerAddPasswordFormProps {
  onPasswordAdded?: () => void;
}

// Add password form schema
const addPasswordFormSchema = z.object({
  newPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  confirmPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type AddPasswordFormValues = z.infer<typeof addPasswordFormSchema>;

const PractitionerAddPasswordForm: React.FC<PractitionerAddPasswordFormProps> = ({ onPasswordAdded }) => {
  const { toast } = useToast();
  const { t } = useTranslation('settings');
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<AddPasswordFormValues>({
    resolver: zodResolver(addPasswordFormSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: AddPasswordFormValues) => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: values.newPassword
      });
      
      if (error) throw error;
      
      toast({
        title: t('security.passwordAdded'),
        description: t('security.passwordAddedDescription'),
      });
      
      form.reset();
      
      // Notify parent component
      if (onPasswordAdded) {
        onPasswordAdded();
      }
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: t('security.updateFailed'),
        description: error.message || t('security.updateFailedDescription'),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/50">
      <CardHeader>
        <CardTitle className="text-amber-900">{t('security.addPassword')}</CardTitle>
        <CardDescription className="text-amber-800">
          {t('security.addPasswordDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="newPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('security.newPassword')}</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder={t('security.newPasswordPlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('security.confirmPassword')}</FormLabel>
                  <FormControl>
                    <PasswordInput placeholder={t('security.confirmPasswordPlaceholder')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button 
              type="submit" 
              disabled={isLoading}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('security.settingPassword')}
                </>
              ) : (
                t('security.setPassword')
              )}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default PractitionerAddPasswordForm;
